import React, { useEffect, useMemo, useRef, useState } from 'react';
import dateOptions from 'components/global/Constant/dateOptions';
import { Button, Card, Col, Collapse, Fade, Form, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useLocation } from 'react-router-dom';
import PaginationItems from 'components/global/Pagination';
import { ascSortIcon, descSortIcon, sortIcon } from 'components/global/Constant/Icons';
import portfolioApplicationsSignal from 'signals/EdoPortfolio.signal';
import history from '../../../../history';
import applicationDetailSignal from '../../ApplicationDetail/ApplicationDetail.signal';
import {
  handleSendNoteSubmission,
  handleUpdateNoteSubmission,
} from './ApprovableNotesTab.helpers';
import { fetchApplicationNotes } from '../../ApplicationDetail/ApplicationDetail.helpers';
import loaderSignal from 'signals/Loader.signal';
import FilePreviewButton from '../../utils/FilesAndDocuments/PreviewButton/FilePreviewButton';
import { ApplicationNote, ApplicationNoteUser, ProgramRequestNote } from '../../../../types';
import $appSettings from 'signals/AppSettings.signal';
import userAccountSignal from 'signals/UserAccount.signal';

const formatNoteUserName = (user: ApplicationNoteUser | null | undefined): string => {
  if (!user) return 'Unknown';
  return user.displayName?.trim() || [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || 'Unknown';
};

/** Same default as `recordsPerPage` in EdoPortfolio.signal (portfolio table). */
const NOTES_PAGE_QUERY_PARAM = 'notesPage';

/** `key-asc` | `key-desc` — avoids clashing with portfolio `sort`. */
const NOTES_SORT_QUERY_PARAM = 'notesSort';

type NotesSortKey =
  | 'createdDate'
  | 'createdBy'
  | 'visibility'
  | 'content'
  | 'updatedDate'
  | 'updatedBy';

function parseNotesSort(
  search: string,
  visibilityColumnVisible: boolean
): { key: NotesSortKey; dir: 'asc' | 'desc' } | null {
  const raw = new URLSearchParams(search).get(NOTES_SORT_QUERY_PARAM);
  if (!raw) {
    return null;
  }
  const parts = raw.split('-');
  if (parts.length < 2) {
    return null;
  }
  const dir = parts.pop() as string;
  const key = parts.join('-') as NotesSortKey;
  if (dir !== 'asc' && dir !== 'desc') {
    return null;
  }
  const valid: NotesSortKey[] = [
    'createdDate',
    'createdBy',
    'visibility',
    'content',
    'updatedDate',
    'updatedBy',
  ];
  if (!valid.includes(key)) {
    return null;
  }
  if (key === 'visibility' && !visibilityColumnVisible) {
    return null;
  }
  return { key, dir: dir as 'asc' | 'desc' };
}

function compareNotes(
  a: ApplicationNote | ProgramRequestNote,
  b: ApplicationNote | ProgramRequestNote,
  key: NotesSortKey,
  modelType: 'application' | 'programRequest' | null
): number {
  const isApp = (n: ApplicationNote | ProgramRequestNote) =>
    modelType === 'application' && Boolean((n as ApplicationNote).uuid);

  switch (key) {
    case 'createdDate':
      return (
        new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
      );
    case 'updatedDate':
      return (
        new Date(a.updatedDate).getTime() - new Date(b.updatedDate).getTime()
      );
    case 'createdBy': {
      const sa = isApp(a)
        ? formatNoteUserName((a as ApplicationNote).createdBy)
        : '';
      const sb = isApp(b)
        ? formatNoteUserName((b as ApplicationNote).createdBy)
        : '';
      return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
    }
    case 'updatedBy': {
      const sa =
        isApp(a) && (a as ApplicationNote).updatedBy
          ? formatNoteUserName((a as ApplicationNote).updatedBy)
          : '';
      const sb =
        isApp(b) && (b as ApplicationNote).updatedBy
          ? formatNoteUserName((b as ApplicationNote).updatedBy)
          : '';
      return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
    }
    case 'visibility': {
      const na = Number(Boolean((a as ApplicationNote).internalUseOnly));
      const nb = Number(Boolean((b as ApplicationNote).internalUseOnly));
      return na - nb;
    }
    case 'content': {
      const sa = (a.content || '').trim();
      const sb = (b.content || '').trim();
      return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
    }
    default:
      return 0;
  }
}

function stableNoteId(note: ApplicationNote | ProgramRequestNote): string {
  const u = (note as ApplicationNote).uuid;
  if (u) {
    return u;
  }
  const id = (note as ProgramRequestNote).id;
  return id != null ? String(id) : '';
}

const ApprovableNotesTab = () => {
  const {
    id,
    notes,
    applicationComments = [],
    status,
    modelType,
    uploadDocumentType,
    currentTab,
  } = applicationDetailSignal.value;
  const { APPLICATION_STATUS, PORTAL_TYPES } = $appSettings.value.constants;
  const { userData } = userAccountSignal.value;
  const isEdoPortal = userData.account.portalType === PORTAL_TYPES.edo;
  const showInternalColumn = isEdoPortal && modelType === 'application';
  const { isContentLoading } = loaderSignal.value;

  useEffect(() => {
    if (currentTab === 'notes' && modelType === 'application') {
      void fetchApplicationNotes();
    }
  }, [currentTab, modelType]);

  const location = useLocation();
  const recordsPerPage = portfolioApplicationsSignal.value.recordsPerPage;
  const notesPageFromUrl = useMemo(() => {
    const raw = new URLSearchParams(location.search).get(NOTES_PAGE_QUERY_PARAM);
    const n = raw ? Number(raw) : 1;
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }, [location.search]);

  const notesSortState = useMemo(
    () => parseNotesSort(location.search, showInternalColumn),
    [location.search, showInternalColumn]
  );

  const sortedNotes = useMemo(() => {
    if (!notes?.length) {
      return [];
    }
    if (!notesSortState) {
      return notes;
    }
    const { key, dir } = notesSortState;
    const mult = dir === 'asc' ? 1 : -1;
    const list = [...notes];
    list.sort((a, b) => {
      const primary = compareNotes(a, b, key, modelType);
      if (primary !== 0) {
        return primary * mult;
      }
      return stableNoteId(a).localeCompare(stableNoteId(b));
    });
    return list;
  }, [notes, notesSortState, modelType]);

  const totalNotePages = Math.max(
    1,
    Math.ceil((sortedNotes?.length || 0) / recordsPerPage)
  );
  const currentNotesPage = Math.min(notesPageFromUrl, totalNotePages);

  const pagedNotes = useMemo(() => {
    if (!sortedNotes.length) {
      return [];
    }
    const start = (currentNotesPage - 1) * recordsPerPage;
    return sortedNotes.slice(start, start + recordsPerPage);
  }, [sortedNotes, currentNotesPage, recordsPerPage]);

  useEffect(() => {
    if (sortedNotes.length === 0) {
      return;
    }
    if (notesPageFromUrl > totalNotePages) {
      const sp = new URLSearchParams(location.search);
      sp.set(NOTES_PAGE_QUERY_PARAM, String(totalNotePages));
      history.replace(`${location.pathname}?${sp.toString()}`);
    }
  }, [
    sortedNotes.length,
    notesPageFromUrl,
    totalNotePages,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (showInternalColumn) {
      return;
    }
    const sp = new URLSearchParams(location.search);
    const raw = sp.get(NOTES_SORT_QUERY_PARAM);
    if (raw?.startsWith('visibility-')) {
      sp.delete(NOTES_SORT_QUERY_PARAM);
      history.replace(`${location.pathname}?${sp.toString()}`);
    }
  }, [showInternalColumn, location.pathname, location.search]);

  const toggleNotesSort = (columnKey: NotesSortKey) => {
    const sp = new URLSearchParams(location.search);
    const current = parseNotesSort(location.search, showInternalColumn);
    let next: string | null;
    if (!current || current.key !== columnKey) {
      next = `${columnKey}-asc`;
    } else if (current.dir === 'asc') {
      next = `${columnKey}-desc`;
    } else {
      next = null;
    }
    if (next) {
      sp.set(NOTES_SORT_QUERY_PARAM, next);
    } else {
      sp.delete(NOTES_SORT_QUERY_PARAM);
    }
    sp.set(NOTES_PAGE_QUERY_PARAM, '1');
    history.push(`${location.pathname}?${sp.toString()}`);
  };

  const sortIconFor = (columnKey: NotesSortKey): IconProp => {
    if (!notesSortState || notesSortState.key !== columnKey) {
      return sortIcon as IconProp;
    }
    return (notesSortState.dir === 'asc' ? ascSortIcon : descSortIcon) as IconProp;
  };

  const newNoteButtonDisabled =
    status === APPLICATION_STATUS?.inProgress ||
    status === APPLICATION_STATUS?.denied ||
    isContentLoading;

  const newNoteButtonDisabledTitle = (() => {
    if (!newNoteButtonDisabled) {
      return undefined;
    }
    if (isContentLoading) {
      return 'Please wait until the page finishes loading.';
    }
    if (status === APPLICATION_STATUS?.inProgress) {
      return 'Notes cannot be created while the application is a Draft.';
    }
    if (status === APPLICATION_STATUS?.denied) {
      return 'Notes cannot be created when the application is Denied.';
    }
    return 'A new note cannot be created at this time.';
  })();

  const renderSortableTh = (
    columnKey: NotesSortKey,
    title: string,
    style?: React.CSSProperties,
    className?: string
  ) => (
    <th
      style={{ cursor: 'pointer', ...style }}
      className={['text-nowrap', className].filter(Boolean).join(' ')}
      onClick={() => toggleNotesSort(columnKey)}
    >
      <span className="d-inline-flex align-items-center flex-nowrap">
        <span>{title}</span>
        <FontAwesomeIcon
          icon={sortIconFor(columnKey)}
          className="ml-1"
          style={{ flexShrink: 0 }}
        />
      </span>
    </th>
  );

  const [addNoteFormVisible, setAddFormVisible] = useState<boolean>(false);
  const [noteBody, setNoteBody] = useState<string>('');
  const [newInternalUseOnly, setNewInternalUseOnly] = useState<boolean>(false);
  const [legacyNotesOpen, setLegacyNotesOpen] = useState<boolean>(false);
  const [editingNoteUuid, setEditingNoteUuid] = useState<string | null>(null);
  const [editNoteBody, setEditNoteBody] = useState<string>('');
  const [editInternalUseOnly, setEditInternalUseOnly] = useState<boolean>(false);
  const newNoteWrapperRef = useRef<HTMLDivElement | null>(null);
  const editNoteWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (addNoteFormVisible && newNoteWrapperRef.current) {
      const textarea = newNoteWrapperRef.current.querySelector('textarea');
      if (textarea) {
        const t = setTimeout(() => textarea.focus(), 50);
        return () => clearTimeout(t);
      }
    }
  }, [addNoteFormVisible]);

  useEffect(() => {
    if (editingNoteUuid && editNoteWrapperRef.current) {
      const textarea = editNoteWrapperRef.current.querySelector('textarea');
      if (textarea) {
        const t = setTimeout(() => textarea.focus(), 50);
        return () => clearTimeout(t);
      }
    }
  }, [editingNoteUuid]);

  return (
    <>
      {applicationComments.length > 0 && (
        <Row>
          <Col>
            <Button
              variant="link"
              className="p-0 text-left font-weight-bold h5 text-dark"
              onClick={() => setLegacyNotesOpen((o) => !o)}
              aria-expanded={legacyNotesOpen}
            >
              Legacy Notes (imported) {legacyNotesOpen ? '▼' : '▶'}
            </Button>
            <Collapse in={legacyNotesOpen}>
              <div>
                <Card className="px-16 py-8 rounded-0 mt-2">
                  {applicationComments.map((c) => (
                    <div className="bg-light rounded p-1 mb-2" key={c.id}>
                      <p className="mb-0 d-flex align-items-center flex-wrap">
                        <span className="mr-1">
                          {new Date(c.createdDate).toLocaleDateString('en-US', dateOptions.date as Intl.DateTimeFormatOptions)}
                        </span>
                        {c.commentType && (
                          <span className="badge badge-secondary mr-1">
                            {c.commentType}
                          </span>
                        )}
                        <span className="text-muted">
                          –{' '}
                          {c.createdBy
                            ? (c.createdBy.displayName || [c.createdBy.firstName, c.createdBy.lastName].filter(Boolean).join(' ') || 'Unknown')
                            : 'Unknown'}
                        </span>
                      </p>
                      {c.comments && (
                        <p className="mb-0 mt-1" style={{ whiteSpace: 'pre-line' }}>
                          {c.comments}
                        </p>
                      )}
                    </div>
                  ))}
                </Card>
              </div>
            </Collapse>
          </Col>
        </Row>
      )}

      <Row className={applicationComments.length > 0 ? 'mt-3' : undefined}>
        <Col>
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="mb-0">Notes</h5>
            </Col>
            <Col xs="auto" className="note-header-buttons">
              <style>{`
                .note-header-buttons button:focus:not(:focus-visible) {
                  outline: none;
                  box-shadow: none;
                }
              `}</style>
              {addNoteFormVisible ? (
                <>
                  <Button
                    variant="secondary"
                    className="rounded mr-2"
                    onClick={() => {
                      if (noteBody.trim()) {
                        if (window.confirm('You have unsaved text in the new note. Discard and close?')) {
                          setAddFormVisible(false);
                          setNoteBody('');
                          setNewInternalUseOnly(false);
                        }
                      } else {
                        setAddFormVisible(false);
                        setNoteBody('');
                        setNewInternalUseOnly(false);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      status === APPLICATION_STATUS?.denied ||
                      isContentLoading ||
                      !noteBody
                    }
                    variant="primary"
                    className="rounded"
                    onClick={() =>
                      handleSendNoteSubmission(noteBody, {
                        internalUseOnly: newInternalUseOnly,
                      }).then(() => {
                        setNoteBody('');
                        setNewInternalUseOnly(false);
                        setAddFormVisible(false);
                      })
                    }
                  >
                    Send Note
                  </Button>
                </>
              ) : (
                <span
                  className="d-inline-block"
                  title={newNoteButtonDisabledTitle}
                  style={
                    newNoteButtonDisabled ? { cursor: 'not-allowed' } : undefined
                  }
                >
                  <Button
                    disabled={newNoteButtonDisabled}
                    variant="primary"
                    className="rounded"
                    style={
                      newNoteButtonDisabled
                        ? { pointerEvents: 'none' }
                        : undefined
                    }
                    onClick={(e) => {
                      (e.currentTarget as HTMLElement).blur();
                      setNewInternalUseOnly(false);
                      setAddFormVisible(true);
                    }}
                  >
                    New Note
                  </Button>
                </span>
              )}
            </Col>
          </Row>
          <div className="mb-2">
            <Collapse in={addNoteFormVisible}>
              <div ref={newNoteWrapperRef}>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="New note content..."
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                />
                {showInternalColumn && (
                  <Form.Check
                    type="checkbox"
                    id="new-note-internal-only"
                    className="mt-2 mb-0"
                    checked={newInternalUseOnly}
                    onChange={(e) => setNewInternalUseOnly(e.target.checked)}
                    label="Internal use only (Not visible to Lender or Business)"
                  />
                )}
              </div>
            </Collapse>
          </div>
          <Table bordered responsive className="mb-0">
            <thead>
              <tr>
                {renderSortableTh('createdDate', 'Created', { width: '140px' })}
                {renderSortableTh('createdBy', 'Created By', { width: '120px' })}
                {showInternalColumn &&
                  renderSortableTh('visibility', 'Visibility', { minWidth: '118px' })}
                {renderSortableTh('content', 'Note')}
                {renderSortableTh('updatedDate', 'Last Updated', { width: '140px' })}
                {renderSortableTh('updatedBy', 'Updated By', { width: '120px' })}
                <th style={{ width: '80px' }} className="text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {notes.length === 0 ? (
                <tr>
                  <td colSpan={showInternalColumn ? 7 : 6} className="text-center text-muted py-4">
                    No notes yet.
                  </td>
                </tr>
              ) : (
              pagedNotes.map((note) => {
                const noteUuid = (note as ApplicationNote).uuid;
                const isApplicationNote = modelType === 'application' && noteUuid;
                const isEditing = editingNoteUuid === noteUuid;
                const updatedByLabel = isApplicationNote
                  ? (note as ApplicationNote).updatedBy
                    ? formatNoteUserName((note as ApplicationNote).updatedBy)
                    : '—'
                  : '—';
                return (
                  <tr key={(note as ProgramRequestNote).id || (note as ApplicationNote).uuid}>
                    <td className="text-nowrap align-top">
                      {new Date(note.createdDate).toLocaleString('en-US', dateOptions.dateTimeFormatted as Intl.DateTimeFormatOptions)}
                    </td>
                    <td className="align-top text-nowrap">
                      {isApplicationNote
                        ? formatNoteUserName((note as ApplicationNote).createdBy)
                        : '—'}
                    </td>
                    {showInternalColumn && (
                      <td className="align-top text-nowrap">
                        {(note as ApplicationNote).internalUseOnly ? (
                          <span
                            className="badge badge-secondary"
                            title="Not visible to Lender or Business"
                          >
                            Internal
                          </span>
                        ) : (
                          <span
                            className="badge badge-light text-dark border"
                            title="Visible to Lender or Business"
                          >
                            Shared
                          </span>
                        )}
                      </td>
                    )}
                    <td className="align-top">
                      {isEditing ? (
                        <Fade in appear>
                          <div ref={editNoteWrapperRef}>
                            <Form.Control
                              as="textarea"
                              value={editNoteBody}
                              onChange={(e) => setEditNoteBody(e.target.value)}
                              className="mb-2"
                              rows={3}
                            />
                            {showInternalColumn && (
                              <Form.Check
                                type="checkbox"
                                id={`edit-note-internal-${noteUuid}`}
                                className="mb-2"
                                checked={editInternalUseOnly}
                                onChange={(e) => setEditInternalUseOnly(e.target.checked)}
                                label="Internal use only (Not visible to Lender or Business)"
                              />
                            )}
                            <div className="d-flex">
                              <Button
                                variant="secondary"
                                size="sm"
                                className="mr-2"
                                onClick={() => {
                                  setEditingNoteUuid(null);
                                  setEditNoteBody('');
                                  setEditInternalUseOnly(false);
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                disabled={
                                  isContentLoading ||
                                  !editNoteBody.trim() ||
                                  (editNoteBody.trim() === (note.content || '').trim() &&
                                    editInternalUseOnly ===
                                      Boolean((note as ApplicationNote).internalUseOnly))
                                }
                                onClick={() =>
                                  handleUpdateNoteSubmission(
                                    noteUuid!,
                                    editNoteBody.trim(),
                                    editInternalUseOnly
                                  ).then(() => {
                                    setEditingNoteUuid(null);
                                    setEditNoteBody('');
                                    setEditInternalUseOnly(false);
                                  })
                                }
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        </Fade>
                      ) : (
                        <>
                          <span className="mb-0">{note.content}</span>
                          {(note.attachments || []).length > 0 && (
                            <div className="mt-1">
                              {(note.attachments || []).map((attachment, index) => (
                                // @ts-ignore
                                <React.Fragment key={attachment.uuid}>
                                  <FilePreviewButton
                                    render={(submitting, fn) => (
                                      <span
                                        className="text-secondary mr-2 small"
                                        style={{
                                          cursor: 'pointer',
                                          textDecoration: 'underline',
                                        }}
                                        onClick={() =>
                                          fn(
                                            { ...attachment, parentId: id },
                                            uploadDocumentType!
                                          )
                                        }
                                      >
                                        {`Attachment ${index + 1}`}
                                      </span>
                                    )}
                                  />
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="text-nowrap align-top">
                      {new Date(note.updatedDate).toLocaleString('en-US', dateOptions.dateTimeFormatted as Intl.DateTimeFormatOptions)}
                    </td>
                    <td className="align-top text-nowrap">{updatedByLabel}</td>
                    <td className="text-center align-top">
                      {!isEditing && isApplicationNote && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setEditingNoteUuid(noteUuid!);
                            setEditNoteBody(note.content);
                            setEditInternalUseOnly(
                              Boolean((note as ApplicationNote).internalUseOnly)
                            );
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </Table>
          {notes.length > 0 && totalNotePages > 1 && (
            <PaginationItems
              className="justify-content-center mt-24"
              totalPages={totalNotePages}
              pageParam={NOTES_PAGE_QUERY_PARAM}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default ApprovableNotesTab;
